import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { find, map } from 'underscore';
import { useBlockScroll } from '../hooks/useBlockScroll';
import { Loader } from 'app/components/loader';
import classNames from 'classnames';
import { setOpenMenu } from '../actions/openMenu';
import { useLocation } from 'react-router';
import { LendingActions } from 'app/modules/lending/components/actions';

const ParentMenuItem = forwardRef(({ name, onClick, active, ...rest }, ref) => {
    return (
        <Link
            ref={ref}
            {...rest}
            onClick={onClick}
            className={classNames('menu_desktop__category', {
                menu_desktop__category_active: active,
            })}
        >
            {name}
        </Link>
    );
});

const ColumnItem = ({ items, name, to }) => {
    return (
        <div className={'menu_desktop__sub-category'}>
            <Link to={to}>
                <span className={'menu_desktop__sub-category__header'}>
                    {name}
                </span>
            </Link>
            {map(items, ({ name, id }) => (
                <Link key={id} to={`categories/${id}`}>
                    <div className={'menu_desktop__sub-category'}>{name}</div>
                </Link>
            ))}
        </div>
    );
};

const Column = ({ categories }) => {
    return (
        <div className={'menu_desktop__column'}>
            {map(categories, ({ children, name, id }) => (
                <ColumnItem
                    key={id}
                    items={children}
                    name={name}
                    to={`categories/${id}`}
                />
            ))}
        </div>
    );
};

function splitToChunks(array, parts) {
    const copy = JSON.parse(JSON.stringify(array));
    let result = [];
    for (let i = parts; i > 0; i--) {
        result.push(copy.splice(0, Math.ceil(copy.length / i)));
    }
    return result;
}

const MenuDesktop = ({
    setting,
    types,
    activeTypeGlobal,
    setOpenMenu,
    onSave,
    onEdit,
    onCancel,
    onDiscard,
    isAdmin,
    settings,
    typesChanged,
    isFetching: isAddedFetching,
}) => {
    const [activeCategory, setActiveCategory] = useState(null);
    useBlockScroll();
    const { response, isFetching } = types;

    useEffect(() => {
        if (Array.isArray(settings) && settings[0])
            setActiveCategory(settings[0]);
    }, [settings]);

    const [first, second, third] = useMemo(() => {
        if (!activeCategory) return [];
        return splitToChunks(activeCategory?.children || [], 3);
    }, [activeCategory, settings]);

    const ref = useRef({});

    useEffect(() => {
        if (activeCategory?.id === activeCategory) return;
        const findType = find(settings, (type) => type.id === activeTypeGlobal);
        if (findType) setActiveCategory(findType);
    }, [activeTypeGlobal, response?.lists]);

    useEffect(() => {
        const type = ref.current[activeTypeGlobal];
        if (type) type?.scrollIntoView();
    }, []);

    const location = useLocation();
    const prevLocation = useRef(location.pathname);

    useEffect(() => {
        if (prevLocation.current !== location.pathname) setOpenMenu(false);
        prevLocation.current = location.pathname;
    }, [location.pathname]);

    return (
        <div
            style={{
                background: setting.content.primary.background,
            }}
            className={'menu_desktop__container'}
        >
            {isFetching || isAddedFetching ? (
                <Loader />
            ) : (
                <div className={'menu_desktop'}>
                    <div
                        className={classNames(
                            'menu_desktop__categories-wrapper',
                            {
                                'menu_desktop__categories-wrapper_changed':
                                    typesChanged,
                            }
                        )}
                    >
                        {isAdmin ? (
                            <LendingActions
                                onEdit={onEdit}
                                onCancel={onCancel}
                                onDiscard={onDiscard}
                                onSave={onSave}
                            />
                        ) : null}
                        <div className={'menu_desktop__categories'}>
                            {map(settings, (category) => {
                                const { id, name } = category;
                                return (
                                    <ParentMenuItem
                                        ref={(e) => {
                                            ref.current[id] = e;
                                        }}
                                        to={`/categories/${id}`}
                                        active={
                                            id === activeCategory?.id ||
                                            id === activeTypeGlobal
                                        }
                                        name={name}
                                        key={id}
                                        onClick={() => setOpenMenu(false)}
                                        onMouseOver={() =>
                                            setActiveCategory(category)
                                        }
                                    />
                                );
                            })}
                        </div>
                    </div>
                    <div className={'menu_desktop__sub-categories'}>
                        <span
                            className={'menu_desktop__sub-categories__header'}
                        >
                            {activeCategory?.name}
                        </span>
                        <div className={'menu_desktop__columns'}>
                            <Column categories={first} />
                            <Column categories={second} />
                            <Column categories={third} />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const mapStateToProps = (state) => {
    return {
        activeTypeGlobal: state.shopState.active_type,
        types: state.shopState.types,
        setting: state.appState.setting,
        point: state.shopState.order.point,
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        setOpenMenu: (data) => dispatch(setOpenMenu(data)),
    };
};

export const ConnectedMenuDesktop = connect(
    mapStateToProps,
    mapDispatchToProps
)(MenuDesktop);
